import React, { useEffect, useMemo, useState } from "react";

export type TimeSlot = { start: string; end: string };

const parseTime = (t: string): number => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const formatTime = (mins: number): string => {
  const totalMins = Math.max(0, Math.min(mins, 24 * 60));
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;
  
  if (hours === 24) {
    return "24:00";
  }
  
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

export type UseSlotSelectorResult = {
  slots: TimeSlot[];
  slotStartTime: string;
  slotEndTime: string;
  nextStartTimeSlot: () => void;
  prevStartTimeSlot: () => void;
  nextEndTimeSlot: () => void;
  prevEndTimeSlot: () => void;
  setSlotStartTime: React.Dispatch<React.SetStateAction<string>>;
  setSlotEndTime: React.Dispatch<React.SetStateAction<string>>;
  canGoNextStart: boolean;
  canGoPrevStart: boolean;
  canGoNextEnd: boolean;
  canGoPrevEnd: boolean;
  isCurrentSlotValid: boolean;
};

function useSlotSelector(
  startTime: string,
  endTime: string,
  slotDuration = 60,
  slotGap = 30,
  minDuration = 60,
  maxDuration = 180,
  selectedSlots: TimeSlot[] = []
): UseSlotSelectorResult {
  const slots = useMemo(() => {
    const startMin = parseTime(startTime);
    const endMin = parseTime(endTime);
    const arr: TimeSlot[] = [];
    let currentStart = startMin;
    while (currentStart + slotDuration <= endMin) {
      const s = formatTime(currentStart);
      const e = formatTime(currentStart + slotDuration);
      
      // Check if this slot overlaps with any selected slot
      const hasOverlap = selectedSlots.some(selectedSlot => {
        const selectedStart = parseTime(selectedSlot.start);
        const selectedEnd = parseTime(selectedSlot.end);
        // Check for any overlap: current slot starts before selected ends AND current slot ends after selected starts
        return currentStart < selectedEnd && (currentStart + slotDuration) > selectedStart;
      });
      
      if (!hasOverlap) {
        arr.push({ start: s, end: e });
      }
      currentStart += slotGap;
    }
    return arr;
  }, [startTime, endTime, slotDuration, slotGap, selectedSlots]);

  const defaultStart = slots.length > 0 ? slots[0].start : startTime;
  const defaultEndMins = parseTime(defaultStart) + Math.min(minDuration, maxDuration);

  const [slotStartTime, setSlotStartTime] = useState(defaultStart);
  const [slotEndTime, setSlotEndTime] = useState(formatTime(defaultEndMins));

  // Update slot times when slots change (e.g., when selectedSlots changes)
  useEffect(() => {
    if (slots.length > 0) {
      // Always use the first available slot when slots change
      const firstSlot = slots[0];
      setSlotStartTime(firstSlot.start);
      const endMins = parseTime(firstSlot.start) + Math.min(minDuration, maxDuration);
      setSlotEndTime(formatTime(endMins));
    }
  }, [slots, minDuration, maxDuration]);

  const nextStartTimeSlot = () => {
    const slotStarts = slots.map(s => s.start);
    const idx = slotStarts.indexOf(slotStartTime);
    if (idx < slotStarts.length - 1) {
      const newStart = slotStarts[idx + 1];
      const newStartMin = parseTime(newStart);
      const duration = parseTime(slotEndTime) - parseTime(slotStartTime);
      const cappedDuration = Math.min(Math.max(duration, minDuration), maxDuration);
      const newEndMin = newStartMin + cappedDuration;
      
      // Check if the new slot would overlap with any existing slots
      const wouldOverlap = selectedSlots.some(selectedSlot => {
        const selectedStart = parseTime(selectedSlot.start);
        const selectedEnd = parseTime(selectedSlot.end);
        return newStartMin < selectedEnd && newEndMin > selectedStart;
      });
      
      if (!wouldOverlap) {
        setSlotStartTime(newStart);
        setSlotEndTime(formatTime(newEndMin));
      }
    } else {
      // If at the end, try to find the next available slot after current end time
      const currentEndMin = parseTime(slotEndTime);
      const nextSlot = slots.find(slot => parseTime(slot.start) > currentEndMin);
      if (nextSlot) {
        const nextStartMin = parseTime(nextSlot.start);
        const duration = parseTime(slotEndTime) - parseTime(slotStartTime);
        const cappedDuration = Math.min(Math.max(duration, minDuration), maxDuration);
        const newEndMin = Math.min(nextStartMin + cappedDuration, 24 * 60);
        
        // Check if the new slot would overlap with any existing slots
        const wouldOverlap = selectedSlots.some(selectedSlot => {
          const selectedStart = parseTime(selectedSlot.start);
          const selectedEnd = parseTime(selectedSlot.end);
          return nextStartMin < selectedEnd && newEndMin > selectedStart;
        });
        
        if (!wouldOverlap) {
          setSlotStartTime(nextSlot.start);
          setSlotEndTime(formatTime(newEndMin));
        }
      }
    }
  };

  const prevStartTimeSlot = () => {
    const slotStarts = slots.map(s => s.start);
    const idx = slotStarts.indexOf(slotStartTime);
    if (idx > 0) {
      const newStart = slotStarts[idx - 1];
      const newStartMin = parseTime(newStart);
      const duration = parseTime(slotEndTime) - parseTime(slotStartTime);
      const cappedDuration = Math.min(Math.max(duration, minDuration), maxDuration);
      setSlotStartTime(newStart);
      setSlotEndTime(formatTime(Math.min(newStartMin + cappedDuration, 24 * 60)));
    }
  };

  const nextEndTimeSlot = () => {
    const curStart = parseTime(slotStartTime);
    const curEnd = parseTime(slotEndTime);
    
    // Find the next slot start time after current end time
    const nextSlot = slots.find(slot => parseTime(slot.start) > curEnd);
    const maxEnd = Math.min(curStart + maxDuration, 24 * 60);
    
    if (nextSlot) {
      // Set end time to the minimum of: next slot start time OR max duration
      const nextStartMin = parseTime(nextSlot.start);
      const newEnd = Math.min(nextStartMin, maxEnd);
      const minEnd = curStart + minDuration;
      if (newEnd >= minEnd) {
        setSlotEndTime(formatTime(newEnd));
      }
    } else {
      // Fallback to incrementing by slot gap if no next slot found, but respect max duration
      const newEnd = Math.min(curEnd + slotGap, maxEnd);
      const minEnd = curStart + minDuration;
      if (newEnd >= minEnd) {
        setSlotEndTime(formatTime(newEnd));
      }
    }
  };

  const prevEndTimeSlot = () => {
    const curStart = parseTime(slotStartTime);
    const curEnd = parseTime(slotEndTime);
    
    // Find the previous available slot start time before current start time
    const prevSlots = slots.filter(slot => parseTime(slot.start) < curStart).reverse();
    const prevSlot = prevSlots[0]; // Get the last (most recent) slot before current start
    if (prevSlot) {
      const prevStartMin = parseTime(prevSlot.start);
      const duration = curEnd - curStart;
      const cappedDuration = Math.min(Math.max(duration, minDuration), maxDuration);
      setSlotEndTime(formatTime(Math.min(prevStartMin + cappedDuration, 24 * 60)));
    } else {
      // Fallback to decrementing by slot gap if no previous slot found
      const newEnd = Math.max(curEnd - slotGap, curStart + minDuration);
      const minEnd = curStart + minDuration;
      if (newEnd >= minEnd) {
        setSlotEndTime(formatTime(newEnd));
      }
    }
  };

  // Calculate button states
  const slotStarts = slots.map(s => s.start);
  const currentStartIdx = slotStarts.indexOf(slotStartTime);
  
  const canGoNextStart = useMemo(() => {
    if (currentStartIdx < slotStarts.length - 1) {
      // Check if next slot would overlap
      const nextStart = slotStarts[currentStartIdx + 1];
      const nextStartMin = parseTime(nextStart);
      const duration = parseTime(slotEndTime) - parseTime(slotStartTime);
      const cappedDuration = Math.min(Math.max(duration, minDuration), maxDuration);
      const newEndMin = Math.min(nextStartMin + cappedDuration, 24 * 60);
      
      const wouldOverlap = selectedSlots.some(selectedSlot => {
        const selectedStart = parseTime(selectedSlot.start);
        const selectedEnd = parseTime(selectedSlot.end);
        return nextStartMin < selectedEnd && newEndMin > selectedStart;
      });
      
      return !wouldOverlap;
    } else {
      // Check if there's a slot after current end time
      const currentEndMin = parseTime(slotEndTime);
      const nextSlot = slots.find(slot => parseTime(slot.start) > currentEndMin);
      if (nextSlot) {
        const nextStartMin = parseTime(nextSlot.start);
        const duration = parseTime(slotEndTime) - parseTime(slotStartTime);
        const cappedDuration = Math.min(Math.max(duration, minDuration), maxDuration);
        const newEndMin = Math.min(nextStartMin + cappedDuration, 24 * 60);
        
        const wouldOverlap = selectedSlots.some(selectedSlot => {
          const selectedStart = parseTime(selectedSlot.start);
          const selectedEnd = parseTime(selectedSlot.end);
          return nextStartMin < selectedEnd && newEndMin > selectedStart;
        });
        
        return !wouldOverlap;
      }
      return false;
    }
  }, [slots, slotStartTime, slotEndTime, selectedSlots, minDuration, maxDuration, currentStartIdx, slotStarts]);

  const canGoPrevStart = useMemo(() => {
    return currentStartIdx > 0;
  }, [currentStartIdx]);

  const canGoNextEnd = useMemo(() => {
    const curStart = parseTime(slotStartTime);
    const curEnd = parseTime(slotEndTime);
    
    // Disable if already at 24:00
    if (curEnd >= 24 * 60) {
      return false;
    }
    
    const maxEnd = Math.min(curStart + maxDuration, 24 * 60);
    
    // Check if there's a next slot after current end time
    const nextSlot = slots.find(slot => parseTime(slot.start) > curEnd);
    if (nextSlot) {
      const nextStartMin = parseTime(nextSlot.start);
      const newEnd = Math.min(nextStartMin, maxEnd);
      const minEnd = curStart + minDuration;
      return newEnd >= minEnd;
    } else {
      // Check if we can increment by slot gap
      const newEnd = Math.min(curEnd + slotGap, maxEnd);
      const minEnd = curStart + minDuration;
      return newEnd >= minEnd;
    }
  }, [slots, slotStartTime, slotEndTime, maxDuration, minDuration, slotGap]);

  const canGoPrevEnd = useMemo(() => {
    const curStart = parseTime(slotStartTime);
    const curEnd = parseTime(slotEndTime);
    
    // Check if there's a previous slot before current start time
    const prevSlots = slots.filter(slot => parseTime(slot.start) < curStart).reverse();
    const prevSlot = prevSlots[0];
    if (prevSlot) {
      const prevStartMin = parseTime(prevSlot.start);
      const duration = curEnd - curStart;
      const cappedDuration = Math.min(Math.max(duration, minDuration), maxDuration);
      const newEnd = Math.min(prevStartMin + cappedDuration, 24 * 60);
      const minEnd = curStart + minDuration;
      return newEnd >= minEnd;
    } else {
      // Check if we can decrement by slot gap
      const newEnd = Math.max(curEnd - slotGap, curStart + minDuration);
      const minEnd = curStart + minDuration;
      return newEnd >= minEnd;
    }
  }, [slots, slotStartTime, slotEndTime, minDuration, maxDuration, slotGap]);

  const isCurrentSlotValid = useMemo(() => {
    const curStart = parseTime(slotStartTime);
    const curEnd = parseTime(slotEndTime);
    
    // Check if current slot overlaps with any selected slots
    const hasOverlap = selectedSlots.some(selectedSlot => {
      const selectedStart = parseTime(selectedSlot.start);
      const selectedEnd = parseTime(selectedSlot.end);
      return curStart < selectedEnd && curEnd > selectedStart;
    });
    
    // Check if duration is within valid range
    const duration = curEnd - curStart;
    const isValidDuration = duration >= minDuration && duration <= maxDuration;
    
    return !hasOverlap && isValidDuration;
  }, [slotStartTime, slotEndTime, selectedSlots, minDuration, maxDuration]);

  return {
    slots,
    slotStartTime,
    slotEndTime,
    nextStartTimeSlot,
    prevStartTimeSlot,
    nextEndTimeSlot,
    prevEndTimeSlot,
    setSlotStartTime,
    setSlotEndTime,
    canGoNextStart,
    canGoPrevStart,
    canGoNextEnd,
    canGoPrevEnd,
    isCurrentSlotValid,
  };
}

export default useSlotSelector;